/* eslint-disable import/no-unused-modules */
import type {
    Proposal,
    ProductItem,
    PaymentItem,
    ProfessionalInfo,
    Contact,
    ProposalTemplate,
    InvoicePaymentStatus,
    Currency,
    InvoiceStatus,
} from '../type/types';
import type { InvoiceColumnProps } from '~/modules/page-builder/component/edit/invoice-module/types';
import type { InvoiceableSignatureFallback } from '~/modules/page-builder/types';
import { getDocType } from './invoice-doc-type';

/**
 * Maps InvoiceModule column data (from page builder modules) to Proposal interface
 * @param columnData - The invoice column data from a page builder module
 * @param urlSegment - The url segment of the proposal
 * @param professionalData - Professional data from the module meta
 * @param signatureFallback - Optional signature data from QuoteContent.Invoiceable when column lacks it
 * @returns Proposal object that matches the expected interface
 */
export const mapInvoiceModuleToProposal = (
    columnData: InvoiceColumnProps,
    urlSegment: string,
    professionalData?: {
        id: number;
        title: string;
    },
    signatureFallback?: InvoiceableSignatureFallback | null,
): Proposal => {
    if (!columnData) {
        throw new Error('Invalid invoice column data');
    }

    // Determine currency from services or default to NZD
    const currency: Currency = columnData.currency || 'NZD';

    // Map professional info (minimal data available from column)
    const professionalInfo: ProfessionalInfo = {
        title: professionalData?.title || '',
        subTitle: '',
        address: '',
        city: '',
        postCode: '',
        state: '',
        bn: '',
    };

    // Map contact info
    const contact: Contact = {
        id: columnData.contact?.id ? parseInt(columnData.contact.id) : 0,
        fullName: columnData.contact?.name || '',
        email: columnData.contact?.email || '',
        firstName: columnData.contact?.firstName || '',
        lastName: columnData.contact?.surname || '',
        companyName: columnData.contact?.companyName || '',
        phone: columnData.contact?.phone || '',
        address: columnData.contact?.address || '',
        postCode: columnData.contact?.postCode || '',
        city: columnData.contact?.city || '',
        hasStripePaymentMethod: false,
    };

    // Map proposal template with tokenized fields
    const proposalTemplate: ProposalTemplate = {
        headline: columnData.headline?.Render ?? columnData.headline?.Raw ?? '',
        subHeadline:
            columnData.subheadline?.Render ?? columnData.subheadline?.Raw ?? '',
        footerLinks: [],
        note: columnData.notes?.Render ?? '',
        contact: contact,
        contactInfo:
            columnData.contactInfo?.Render ?? columnData.contactInfo?.Raw,
        accountDetails:
            columnData.accountDetails?.Render ?? columnData.accountDetails?.Raw,
        paymentTerms: columnData.paymentTerms?.Render,
        creditCardTerms: columnData.creditCardTerms?.Render,
        bankTransferTerms: columnData.bankTransferTerms?.Render,
        references: columnData.references?.Render,
        businessTerms:
            columnData.businessTermsVersions?.map((version) => ({
                title: version.Title || version.Category?.Title || '',
                body:
                    version.termsHtml ||
                    version.Terms?.Render ||
                    version.Terms?.Raw ||
                    '',
                url: version.URL || '',
            })) || [],
        scope: columnData.scope?.Render,
    };

    // Line items: preserve column order (same as GraphQL / InvoiceServiceItem::$default_sort).
    const productItems: ProductItem[] = (columnData.services ?? []).map(
        (service) => {
            const unitPrice = service.unitPrice || 0;
            const discountPerUnit = service.discountAmountPerUnit ?? 0;
            const effectiveUnitPrice = service.discountApplied
                ? Math.max(0, unitPrice - discountPerUnit)
                : unitPrice;
            const quantity = service.quantity || 0;

            return {
                id: service.id ? parseInt(service.id) : 0,
                name: service.title,
                description: service.description || '',
                imageUrl:
                    service.photoSrc ||
                    '/assets/website/ecommerce/images/proposal-product-demo1-60x60.png',
                quantity,
                unitPrice,
                discountedUnitPrice: Math.max(0, unitPrice - discountPerUnit),
                discountAmountPerUnit: discountPerUnit,
                discountApplied: service.discountApplied,
                taxRate: service.taxRate || 0,
                totalPrice: effectiveUnitPrice * quantity,
                totalPriceAfterTax: service.totalPrice || 0,
                currency,
            };
        },
    );

    // Payments: preserve column order (matches GraphQL / payment SortOrder).
    const paymentItems: PaymentItem[] = (columnData.payments ?? []).map(
        (payment, idx) => ({
            id: payment.id ? parseInt(payment.id) : 0,
            name: `Payment ${idx + 1}`,
            status: mapPaymentStatus(payment.status),
            dueDate: payment.dueDate
                ? new Date(payment.dueDate).getTime()
                : null,
            paymentDate: payment.paymentDate
                ? new Date(payment.paymentDate).getTime()
                : null,
            amount: payment.amount || 0,
            currency,
        }),
    );

    // Use financial totals from backend (already calculated correctly)
    const subtotal = columnData.subtotal ?? 0;
    const discounts = columnData.discounts ?? 0;
    const tax = columnData.tax ?? 0;
    const total = columnData.total ?? 0;

    // Map summaries from scope field
    const summaries: string[] = columnData.scope?.Render
        ? columnData.scope.Render.split('\n')
        : [];

    // Default values for fields not available in column data
    const status = columnData.status as InvoiceStatus;
    const quoteDecisionDate =
        columnData.quoteDecisionDate ??
        signatureFallback?.quoteDecisionDate ??
        null;
    const docType = getDocType(status, quoteDecisionDate);
    const isSplitPayment = paymentItems.length > 1;

    return {
        id: parseInt(columnData.id) || 0,
        urlSegment: urlSegment,
        uuid: urlSegment, // Use urlSegment as UUID fallback
        docType,
        isSplitPayment,
        professionalInfo,
        proposalTemplate,
        productItems,
        paymentItems,
        status,
        currency,
        discounts,
        subtotal,
        tax,
        total,
        summaries,
        customer: contact,
        expiryDate: columnData.expiryDate
            ? new Date(columnData.expiryDate).getTime()
            : null,
        invoiceSentDate: columnData.invoiceSentDate
            ? new Date(columnData.invoiceSentDate).getTime()
            : null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        stripeHostedInvoiceUrl: null,
        stripeAccount: columnData.stripeAccount || null,
        publishableKey: columnData.publishableKey || null,
        hasStripePaymentMethod: columnData.hasStripePaymentMethod ?? false,
        hasProcessingPayment: columnData.hasProcessingPayment ?? false,
        poNumber: columnData.poNumber,
        invoiceNumber: columnData.hubspotId,
        startDate: columnData.startDate,
        endDate: columnData.endDate,
        membershipTerm: columnData.membershipTerm ?? '',
        hideMembershipTerm: columnData.hideMembershipTerm ?? false,
        dealOwner: columnData.dealOwner,
        signature: (() => {
            const hasColumnSignature =
                columnData.signatureFullName || columnData.signatureDataUrl;
            const hasFallbackSignature =
                signatureFallback?.signatureFullName ||
                signatureFallback?.signatureDataUrl;
            if (hasColumnSignature) {
                return {
                    fullName: columnData.signatureFullName || '',
                    email: columnData.signatureEmail || '',
                    dataUrl: columnData.signatureDataUrl || '',
                    signedDate: columnData.quoteDecisionDate || '',
                };
            }
            if (hasFallbackSignature) {
                return {
                    fullName: signatureFallback!.signatureFullName || '',
                    email: signatureFallback!.signatureEmail || '',
                    dataUrl: signatureFallback!.signatureDataUrl || '',
                    signedDate: signatureFallback!.quoteDecisionDate || '',
                };
            }
            return undefined;
        })(),
    };
};

/**
 * Maps payment status from page builder to component interface
 * @param status - Payment status from page builder
 * @returns Mapped payment status
 */
const mapPaymentStatus = (
    status: string | null | undefined,
): InvoicePaymentStatus => {
    if (!status) return 'Upcoming';

    switch (status.toLowerCase()) {
        case 'fullypaid':
            return 'FullyPaid';
        case 'pending':
        case 'upcoming':
            return 'Upcoming';
        case 'overdue':
            return 'Overdue';
        case 'canceled':
            return 'Canceled';
        default:
            return 'Upcoming';
    }
};
